import React, { memo, useMemo } from "react";
import { motion } from "framer-motion";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from "recharts";

/* =============================
   🎨 Navy Design Tokens
============================= */
const C = {
  page: "#060C18",
  card: "#0E1C35",
  cardHigh: "#132443",
  border: "rgba(255,255,255,0.06)",
  grid: "rgba(255,255,255,0.05)",
  textPrimary: "#E6EDF7",
  textMuted: "#8FA3BF",
  accentBlue: "#3B82F6",
  accentEmerald: "#10B981"
};

function TransactionChart({ data = [], title }) {
  const { t } = useLanguage();
  const chartTitle = title || t("transactionVolume");

  /* =============================
     🧠 Derived State (Memoized)
  ============================= */
  const hasData = useMemo(() => {
    return (
      data.length > 0 &&
      data.some((d) => d.deposits > 0 || d.withdrawals > 0)
    );
  }, [data]);

  const chartData = useMemo(() => {
    if (data.length > 0) return data;

    // Keep chart structure visible even when there is no backend data yet.
    return [
      { name: "Mon", deposits: 0, withdrawals: 0 },
      { name: "Tue", deposits: 0, withdrawals: 0 },
      { name: "Wed", deposits: 0, withdrawals: 0 },
      { name: "Thu", deposits: 0, withdrawals: 0 },
      { name: "Fri", deposits: 0, withdrawals: 0 },
      { name: "Sat", deposits: 0, withdrawals: 0 },
      { name: "Sun", deposits: 0, withdrawals: 0 },
    ];
  }, [data]);

  const gradients = useMemo(() => (
    <>
      <linearGradient id="colorDeposits" x1="0" y1="0" x2="0" y2="1">
        <stop offset="5%" stopColor={C.accentBlue} stopOpacity={0.35} />
        <stop offset="95%" stopColor={C.accentBlue} stopOpacity={0} />
      </linearGradient>
      <linearGradient id="colorWithdrawals" x1="0" y1="0" x2="0" y2="1">
        <stop offset="5%" stopColor={C.accentEmerald} stopOpacity={0.35} />
        <stop offset="95%" stopColor={C.accentEmerald} stopOpacity={0} />
      </linearGradient>
    </>
  ), []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: "easeOut" }}
      whileHover={{ y: -2 }}
      style={{
        background: C.card,
        border: `1px solid ${C.border}`,
        borderRadius: 16
      }}
      className="p-5 sm:p-6 shadow-lg h-full flex flex-col"
    >
      {/* =============================
           Header
      ============================= */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
        <h3
          className="text-lg font-semibold tracking-tight"
          style={{ color: C.textPrimary }}
        >
          {chartTitle}
        </h3>

        <div className="flex items-center gap-5 text-sm">
          <LegendItem color={C.accentBlue} label={t("deposits")} />
          <LegendItem color={C.accentEmerald} label={t("withdrawals")} />
        </div>
      </div>

      {/* =============================
           Chart Area
      ============================= */}
      <div className="h-[280px] min-h-[280px] flex-1 relative">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={chartData}
            margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
          >
            <defs>{gradients}</defs>

            <CartesianGrid
              strokeDasharray="3 3"
              stroke={C.grid}
            />

            <XAxis
              dataKey="name"
              axisLine={false}
              tickLine={false}
              tick={{ fill: C.textMuted, fontSize: 12 }}
            />

            <YAxis
              axisLine={false}
              tickLine={false}
              domain={[0, "auto"]}
              tick={{ fill: C.textMuted, fontSize: 12 }}
              tickFormatter={(value) => {
                if (value >= 1000000) {
                  return `฿${(value / 1000000).toFixed(1).replace(".0", "")}M`;
                }

                if (value >= 1000) {
                  return `฿${(value / 1000).toFixed(1).replace(".0", "")}K`;
                }

                return `฿${value}`;
              }}
            />

            <Tooltip
              contentStyle={{
                background: C.cardHigh,
                border: `1px solid ${C.border}`,
                borderRadius: 12,
                color: C.textPrimary
              }}
              formatter={(value) => [
                `฿${value.toLocaleString()}`,
                ""
              ]}
            />

            <Area
              type="monotone"
              dataKey="deposits"
              stroke={C.accentBlue}
              strokeWidth={2}
              fillOpacity={1}
              fill="url(#colorDeposits)"
            />

            <Area
              type="monotone"
              dataKey="withdrawals"
              stroke={C.accentEmerald}
              strokeWidth={2}
              fillOpacity={1}
              fill="url(#colorWithdrawals)"
            />
          </AreaChart>
        </ResponsiveContainer>

      </div>
    </motion.div>
  );
}

/* =============================
   Sub Components (Memoized)
============================= */

const LegendItem = memo(({ color, label }) => (
  <div className="flex items-center gap-2">
    <div
      className="w-3 h-3 rounded-full"
      style={{ background: color }}
    />
    <span style={{ color: C.textMuted }}>{label}</span>
  </div>
));

export default memo(TransactionChart);
